import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useTri, EnteteTri } from './TriTableau';
import './PiecesAlertes.css';

// ═══════════════════════════════════════════════════════════════════════════
// Nouveautés Pièces — la file que les réceptionnaires balaient chaque jour.
//
// Le suivi des pièces remontait déjà dans la modale du dossier, mais il fallait
// penser à l'ouvrir pour apprendre qu'une pièce était arrivée. Ici l'évolution
// vient au-devant du comptoir, et la ligne se retire une fois traitée.
//
// « Vu » est INDIVIDUEL (2026-09-07) : la ligne ne disparaît que pour celui
// qui l'a marquée — l'information sert à plusieurs personnes, chacun archive
// pour lui seul.
// ═══════════════════════════════════════════════════════════════════════════

const TYPES = {
  livraison: { label: 'Livraison', classe: 'pal-badge--livraison' },
  anomalie_ouverte: { label: 'Anomalie', classe: 'pal-badge--anomalie' },
  anomalie_resolue: { label: 'Anomalie résolue', classe: 'pal-badge--resolue' },
};

// La nuance porte sur le statut atteint, pas sur le type d'événement : une
// livraison complète et une régression méritent deux couleurs différentes.
const classeLigne = (a) => {
  if (a.type === 'livraison') {
    if (a.nouveau_statut === 'livre') return 'pal-badge--livre';
    if (a.nouveau_statut === 'recu_partiellement') return 'pal-badge--partiel';
    return 'pal-badge--retour';
  }
  return TYPES[a.type]?.classe || '';
};

// Les statuts sont stockés en clé technique : on ne les affiche jamais bruts,
// « recu_partiellement » n'est pas du français.
const LIBELLE_STATUT = {
  en_commande: 'en commande',
  recu_partiellement: 'reçu partiellement',
  livre: 'livré',
};

// Signal de planification (même règle que le Kanban) : planifié aujourd'hui ou
// après = OK ; date passée, absente, ou dossier « à planifier » = à replanifier.
function ymdLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function etatPlanif(a) {
  if (!a.a_planifier && a.date_planification && a.date_planification >= ymdLocal()) {
    const [, m, j] = a.date_planification.split('-');
    return { cls: 'ok', label: `Planifié le ${j}/${m}` };
  }
  return { cls: 'ko', label: 'À replanifier' };
}

const quand = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const minutes = Math.floor((Date.now() - d.getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  if (minutes < 60 * 24) return `il y a ${Math.floor(minutes / 60)} h`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
    + ' · ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

export default function PiecesAlertes({ onOpenIntervention, onCountChange, rafraichir = 0 }) {
  const [alertes, setAlertes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [recherche, setRecherche] = useState('');
  const [filtreType, setFiltreType] = useState('');
  const [busy, setBusy] = useState(null);      // id en cours de traitement
  const [confirmTout, setConfirmTout] = useState(false);
  const tri = useTri(null, 'asc');

  const charger = useCallback(() => {
    setChargement(true);
    api.get('/pieces-alertes')
      .then(r => { setAlertes(r.data || []); setErreur(''); })
      .catch(() => setErreur('Impossible de charger les nouveautés.'))
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => { charger(); }, [charger, rafraichir]);
  useEffect(() => { if (onCountChange) onCountChange(alertes.length); }, [alertes.length, onCountChange]);

  const traiter = async (ids) => {
    setBusy(ids.length === 1 ? ids[0] : 'tout');
    try {
      await api.post('/pieces-alertes/traiter', { ids });
      setAlertes(prev => prev.filter(a => !ids.includes(a.id)));
      setConfirmTout(false);
    } catch {
      setErreur('Le traitement a échoué. Réessayez.');
    } finally {
      setBusy(null);
    }
  };

  const visibles = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return alertes.filter(a => {
      if (filtreType && a.type !== filtreType) return false;
      if (!q) return true;
      return [a.num_or, a.client_nom, a.immat, a.libelle, a.reference_piece]
        .some(v => (v || '').toLowerCase().includes(q));
    });
  }, [alertes, recherche, filtreType]);

  // Tri partagé. Ordre naturel par défaut (le serveur rend le plus récent
  // d'abord) ; « Quand » se trie sur l'horodatage brut et non sur « il y a 2 h »,
  // qui se comparerait alphabétiquement.
  const triees = tri.appliquer(visibles, {
    quand: a => (a.created_at ? Date.parse(a.created_at) : null),
    or: a => a.num_or,
    client: a => a.client_nom,
    immat: a => a.immat,
    evolution: a => a.libelle,
    planif: a => (etatPlanif(a).cls === 'ko' ? 0 : 1),
    detail: a => a.detail || a.reference_piece,
  });

  const ouvrir = (a) => {
    if (!a.intervention_id || !onOpenIntervention) return;
    onOpenIntervention(a.intervention_id);
  };

  return (
    <div className="pal">
      <div className="page-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
        <select
          className={`page-select${filtreType ? ' active-filter' : ''}`}
          value={filtreType}
          onChange={(e) => setFiltreType(e.target.value)}
        >
          <option value="">Toutes les évolutions</option>
          <option value="livraison">Livraison</option>
          <option value="anomalie_ouverte">Anomalie signalée</option>
          <option value="anomalie_resolue">Anomalie résolue</option>
        </select>
        <span className="pal-compteur">
          {alertes.length === 0 ? 'Aucune nouveauté'
            : `${alertes.length} nouveauté${alertes.length > 1 ? 's' : ''}`}
        </span>
        {alertes.length > 0 && (
          <button
            type="button"
            className="pal-tout"
            onClick={() => setConfirmTout(true)}
            disabled={busy === 'tout'}
          >
            Tout marquer comme vu
          </button>
        )}
      </div>

      {confirmTout && (
        <div className="pal-confirm">
          <span>
            Retirer les <strong>{alertes.length}</strong> nouveautés de votre liste ?
            Vos collègues continueront de les voir.
          </span>
          <div className="pal-confirm-actions">
            <button type="button" className="btn-secondary" onClick={() => setConfirmTout(false)}>Annuler</button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => traiter(alertes.map(a => a.id))}
              disabled={busy === 'tout'}
            >
              {busy === 'tout' ? 'Traitement…' : 'Confirmer'}
            </button>
          </div>
        </div>
      )}

      {erreur && <div className="status-error">{erreur}</div>}

      {chargement ? (
        <div className="pal-vide">Chargement…</div>
      ) : visibles.length === 0 ? (
        <div className="pal-vide">
          {alertes.length === 0
            ? 'Rien de neuf sur les pièces des dossiers planifiés. Tout est à jour.'
            : 'Aucune nouveauté ne correspond à cette recherche.'}
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <EnteteTri tri={tri} cle="quand">Quand</EnteteTri>
                <EnteteTri tri={tri} cle="or">N° OR</EnteteTri>
                <EnteteTri tri={tri} cle="client">Client</EnteteTri>
                <EnteteTri tri={tri} cle="immat">Immat.</EnteteTri>
                <EnteteTri tri={tri} cle="evolution">Évolution</EnteteTri>
                <EnteteTri tri={tri} cle="planif">Planification</EnteteTri>
                <EnteteTri tri={tri} cle="detail">Détail</EnteteTri>
                <th className="pal-col-action"></th>
              </tr>
            </thead>
            <tbody>
              {triees.map(a => (
                <tr
                  key={a.id}
                  className={a.intervention_id ? 'pal-row' : ''}
                  onClick={() => ouvrir(a)}
                  title={a.intervention_id ? 'Ouvrir le dossier' : "Le dossier n'est plus au planning"}
                >
                  <td className="pal-quand">{quand(a.created_at)}</td>
                  <td className="pal-or">{a.num_or}</td>
                  <td>{a.client_nom || '—'}</td>
                  <td className="pal-immat">{a.immat || '—'}</td>
                  <td>
                    <span className={`pal-badge ${classeLigne(a)}`}>{a.libelle}</span>
                  </td>
                  <td>
                    {(() => { const e = etatPlanif(a); return <span className={`pal-planif pal-planif--${e.cls}`}>{e.label}</span>; })()}
                  </td>
                  <td className="pal-detail">
                    {a.reference_piece && <span className="pal-ref">{a.reference_piece}</span>}
                    {a.detail || (a.type === 'livraison' && a.ancien_statut
                      ? <span className="pal-muted">depuis « {LIBELLE_STATUT[a.ancien_statut] || a.ancien_statut} »</span>
                      : null)}
                    {a.auteur && <span className="pal-muted"> · {a.auteur}</span>}
                  </td>
                  <td className="pal-col-action">
                    <div className="action-buttons">
                      <button
                        className="btn-icon"
                        title="J'ai vu — retire la ligne pour vous seulement"
                        disabled={busy === a.id}
                        onClick={(e) => { e.stopPropagation(); traiter([a.id]); }}
                      >
                        ✓
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
